import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useNova } from '../lib/store';
import type { ChatMessage, EmailMessage, InteractionRow } from '../lib/types';
import { Drawer } from './admin/kit';
import { Bubble } from './ChatPane';
import { Mail } from './EmailPane';
import { Button, duration, Panel } from './ui';

const STATUS: Record<string, string> = {
  completed: 'Completed',
  wrap_up: 'In wrap-up',
  abandoned: 'Not answered',
  connected: 'Open',
  queued: 'Waiting',
  ringing: 'Offered',
};

/**
 * The chat log or email log: newest first, searchable by customer, email
 * address or subject, each opening to its conversation. Agents see their own;
 * supervisors and QA everyone's (the API decides).
 */
export function ConversationLog({ channel }: { channel: 'chat' | 'email' }) {
  const role = useNova((s) => s.user?.role);
  const [rows, setRows] = useState<InteractionRow[] | null>(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<InteractionRow | null>(null);
  const noun = channel === 'chat' ? 'chats' : 'emails';

  useEffect(() => {
    let live = true;
    const qs = new URLSearchParams({ limit: '100', channel });
    if (search.trim().length >= 2) qs.set('search', search.trim());
    const t = window.setTimeout(() => {
      api<InteractionRow[]>('GET', `/interactions?${qs}`)
        .then((r) => live && setRows(r))
        .catch(() => live && setRows([]));
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [channel, search]);

  return (
    <Panel className="p-5">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-medium">{role === 'agent' ? `Your ${noun}` : channel === 'chat' ? 'Chat log' : 'Email log'}</h1>
          <p className="text-sm text-ink-3">Newest first. Open one to read the conversation.</p>
        </div>
        <input
          placeholder={channel === 'chat' ? 'Search a name or email' : 'Search a name, email or subject'}
          aria-label={`Search ${noun}`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-xl border border-line bg-abyss/60 px-3.5 py-2 text-sm outline-none focus:border-ion"
        />
      </div>
      {rows === null ? (
        <p className="py-10 text-center text-sm text-ink-3">Loading {noun}…</p>
      ) : rows.length === 0 ? (
        <p className="py-10 text-center text-sm text-ink-2">No {noun} match.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm" aria-label={channel === 'chat' ? 'Chat log' : 'Email log'}>
            <thead className="text-ink-3">
              <tr className="border-b border-line">
                <th className="py-2.5 pr-4 font-normal">When</th>
                <th className="py-2.5 pr-4 font-normal">Customer</th>
                {channel === 'email' ? <th className="py-2.5 pr-4 font-normal">Subject</th> : null}
                <th className="py-2.5 pr-4 font-normal">Queue</th>
                <th className="py-2.5 pr-4 font-normal">Agent</th>
                <th className="py-2.5 pr-4 text-right font-normal">Wait</th>
                <th className="py-2.5 pr-4 text-right font-normal">First reply</th>
                <th className="py-2.5 pr-4 font-normal">Outcome</th>
                <th className="py-2.5 font-normal" aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                // Chats from answering; emails from arriving
                const from = channel === 'chat' ? r.answered_at : r.started_at;
                const first = r.first_response_at && from ? (Date.parse(r.first_response_at) - Date.parse(from)) / 1000 : null;
                return (
                  <tr key={r.id} className="border-b border-line/60 last:border-0">
                    <td className="py-3 pr-4 text-ink-2">{new Date(r.started_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td className="py-3 pr-4">
                      <span className="block">{r.customer_name ?? r.customer_email ?? 'Visitor'}</span>
                      {r.customer_name && r.customer_email ? <span className="text-xs text-ink-3">{r.customer_email}</span> : null}
                    </td>
                    {channel === 'email' ? <td className="max-w-[240px] truncate py-3 pr-4 text-ink-2">{r.subject ?? ''}</td> : null}
                    <td className="py-3 pr-4 text-ink-2">{r.queue_name ?? ''}</td>
                    <td className="py-3 pr-4 text-ink-2">{r.agent_name ?? 'Nobody'}</td>
                    <td className="tabular py-3 pr-4 text-right text-ink-2">{r.wait_seconds != null ? duration(r.wait_seconds) : ''}</td>
                    <td className="tabular py-3 pr-4 text-right">{first != null ? duration(Math.max(0, first)) : ''}</td>
                    <td className="py-3 pr-4">
                      <span className={r.status === 'abandoned' ? 'text-break' : ''}>{r.disposition_label ?? STATUS[r.status] ?? r.status}</span>
                    </td>
                    <td className="py-3 text-right">
                      <Button tone="ghost" className="px-2.5 py-1 text-xs" onClick={() => setOpen(r)}>
                        Read
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {open ? <Transcript row={open} onClose={() => setOpen(null)} /> : null}
    </Panel>
  );
}

/** A chat's messages or an email conversation, read-only. */
export function TranscriptBody({ row }: { row: InteractionRow }) {
  const channel = row.channel === 'email' ? 'email' : 'chat';
  const [chat, setChat] = useState<ChatMessage[] | null>(null);
  const [mail, setMail] = useState<EmailMessage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setChat(null);
    setMail(null);
    setError(null);
    if (channel === 'chat') api<ChatMessage[]>('GET', `/chats/${row.id}/messages`).then(setChat, (e: Error) => setError(e.message));
    else api<EmailMessage[]>('GET', `/emails/${row.id}/messages`).then(setMail, (e: Error) => setError(e.message));
  }, [row.id, channel]);
  return (
    <div aria-label="Conversation">
      {error ? (
        <p className="text-sm text-break">{error}</p>
      ) : channel === 'chat' ? (
        chat ? (
          chat.length ? chat.map((m) => <Bubble key={m.id} m={m} />) : <p className="text-sm text-ink-3">No messages are kept for this chat.</p>
        ) : (
          <p className="text-sm text-ink-3">Loading…</p>
        )
      ) : mail ? (
        mail.length ? mail.map((m, n) => <Mail key={m.id} m={m} earlier={m.interactionId !== row.id} last={n === mail.length - 1} />) : <p className="text-sm text-ink-3">No emails are kept for this conversation.</p>
      ) : (
        <p className="text-sm text-ink-3">Loading…</p>
      )}
    </div>
  );
}

/** A chat or email conversation in a side drawer. */
export function Transcript({ row, onClose }: { row: InteractionRow; onClose: () => void }) {
  const who = row.customer_name ?? row.customer_email ?? 'a visitor';
  return (
    <Drawer
      title={row.channel === 'email' ? row.subject ?? `Email from ${who}` : `Chat with ${who}`}
      open
      onClose={onClose}
      footer={
        <Button tone="ghost" className="ml-auto px-3 py-2" onClick={onClose}>
          Close
        </Button>
      }
    >
      <p className="mb-3 text-[12px] text-ink-3">
        {new Date(row.started_at).toLocaleString()} · {row.queue_name ?? 'No queue'} · {row.agent_name ?? 'Not answered'}
        {row.disposition_label ? ` · ${row.disposition_label}` : ''}
      </p>
      {row.notes ? <p className="mb-3 rounded-lg border border-line p-2 text-sm">Notes: {row.notes}</p> : null}
      <TranscriptBody row={row} />
    </Drawer>
  );
}
